import { useEffect, useState } from "react";
import { getProgressClaimInvoice } from "../../services/apiClient.js";
import { formatDate } from "../../utils/date.js";
import IssuerLogo from "../shared/IssuerLogo.jsx";
import PaymentTerms from "./PaymentTerms.jsx";
import { InvoiceQuoteHeader } from "./InvoiceView.jsx";

// A clean, print-ready invoice rendered from a progress claim. Replaces the legacy
// Crystal "print-screen". Issuer details are placeholders for now — in time they come
// from org invoice settings (cf. otclinic's OrgInvoiceSettings), and per-insurer
// templates would key off the debtor (e.g. IAG / NZI formats).

const nzd = new Intl.NumberFormat("en-NZ", { style: "currency", currency: "NZD" });
const money = (v) => nzd.format(Number(v || 0));

// A zero prints as nothing. Only used on the columns 1.0 leaves empty — the
// figure is still zero, it just isn't worth a reader's attention.
const zeroBlank = (value, formatted) => (Number(value) ? formatted : "");

// The REAL GPS details, matching InvoiceView (which took them from the legacy
// .rpt). These were placeholders — "PO Box 0000", "accounts@example.co.nz",
// "GST 000-000-000" — which was survivable while the document looked obviously
// unfinished. Putting the company logo on it (Karen 2026-08-04) removes that
// excuse: a branded tax invoice quoting a fake GST number is worse than an
// unbranded one. Both copies still want moving to org settings, so that
// per-insurer variants can key off the debtor.
const ISSUER = {
  name: "Grayburn Property Services Ltd",
  lines: [
    "2D Rawhiti Rd, One Tree Hill, Auckland",
    "PO Box 11-551, Ellerslie",
    "reception@gps1.co.nz · Ph: 09-622 3900",
    "GST 35-746-343",
  ],
};

// `invoiceTo` / `header` = the job's Invoice To lines and Invoice Quote
// Header (invoices.md §11), passed by the Invoices tab from the invoice read
// so the progress-claim document prints them the same way the tax invoice
// does: the lines replace the Bill To block, the header sits above the lines.
function InvoiceDocument({ claimId, pcId, invoiceTo = null, header = null }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    getProgressClaimInvoice(claimId, pcId)
      .then(setData)
      .catch(() => setError("Unable to load invoice."));
  }, [claimId, pcId]);

  if (error) return <div className="alert alert-danger">{error}</div>;
  if (!data) return <p className="text-muted">Loading…</p>;

  const { claim, progress_claim, invoice_ref, summary, lines, gst_rate, prior_claims = [] } = data;
  const billAddress = claim?.insurer_postal || claim?.insurer_physical;

  // On Progress Claim 1 there is nothing previously claimed, so 1.0 left the
  // column off altogether (Robert 2026-08-04, comparing job 75700 in both).
  // Keyed off the claim number rather than "are all the figures zero", because
  // that is the rule 1.0 applied and a later claim can legitimately show zeros.
  const showPrevious = Number(progress_claim?.claim_no) !== 1;

  return (
    <div className="print-document bg-white text-dark p-4" style={{ maxWidth: 820, margin: "0 auto" }}>
      <div className="d-flex justify-content-between align-items-start mb-4">
        {/* Same letterhead as the tax invoice and the quote: logo and name on
            one line, address underneath. One shape across all three documents. */}
        <div>
          <div className="d-flex align-items-center gap-3">
            <IssuerLogo className="" />
            <div className="fs-4 fw-bold">{ISSUER.name}</div>
          </div>
          <div className="mt-2">
            {ISSUER.lines.map((l, i) => (
              <div key={i} className="small text-muted">{l}</div>
            ))}
          </div>
        </div>
        <div className="text-end">
          <div className="fs-3 fw-bold text-uppercase">Tax Invoice</div>
          <div className="small">Invoice <strong>{invoice_ref}</strong></div>
          <div className="small">Date {formatDate(new Date().toISOString())}</div>
        </div>
      </div>

      <div className="row mb-4">
        <div className="col-6">
          <div className="text-uppercase small text-muted fw-semibold mb-1">Bill To</div>
          {invoiceTo?.length ? (
            invoiceTo.map((l, i) => <div key={i} className={i === 0 ? "fw-semibold" : "small"}>{l}</div>)
          ) : (
            <>
              <div className="fw-semibold">{claim?.insurer_name || "—"}</div>
              {billAddress ? <div className="small" style={{ whiteSpace: "pre-wrap" }}>{billAddress}</div> : null}
            </>
          )}
        </div>
        <div className="col-6">
          <div className="text-uppercase small text-muted fw-semibold mb-1">Reference</div>
          <table className="small">
            <tbody>
              <tr><td className="pe-3 text-muted">Job #</td><td>{claim?.claim_number}</td></tr>
              {claim?.insurance_claim_number ? (
                <tr><td className="pe-3 text-muted">Insurance Claim</td><td>{claim.insurance_claim_number}</td></tr>
              ) : null}
              {claim?.client_name ? <tr><td className="pe-3 text-muted">Client</td><td>{claim.client_name}</td></tr> : null}
              {claim?.street_address ? <tr><td className="pe-3 text-muted">Site</td><td>{claim.street_address}</td></tr> : null}
              <tr><td className="pe-3 text-muted">Progress Claim</td><td>#{progress_claim.claim_no}</td></tr>
            </tbody>
          </table>
        </div>
      </div>

      <InvoiceQuoteHeader text={header} />

      {/* Bordered: Robert put borders on the 1.0 report for exactly this reason —
          seven numeric columns are hard to track across without them. */}
      <table className="table table-sm table-bordered align-middle">
        <thead>
          {/* 1.0's columns (Robert 2026-08-04): Description · Quote · %
              Complete · Total Claim. The Quote and the percentage are the pair
              an assessor actually checks — is this claim proportionate to the
              work done — and 2.0 was printing neither. "Total Claim" is 1.0's
              name for what we already had as Total to Date. Previously Claimed
              / This Claim stay for later cycles, where they are the only thing
              saying what is owed NOW. */}
          <tr style={{ borderBottom: "2px solid #dee2e6" }}>
            <th>Description</th>
            {showPrevious ? <th className="text-end fw-normal text-muted">Prev %</th> : null}
            {showPrevious ? <th className="text-end fw-normal text-muted">Prev Amount</th> : null}
            <th className="text-end">Quote</th>
            <th className="text-end">% Complete</th>
            <th className="text-end">Total Claim</th>
            {showPrevious ? <th className="text-end">This Claim</th> : null}
          </tr>
        </thead>
        <tbody>
          {lines.map((l, i) => (
            <tr key={i}>
              <td>
                {l.description || "—"}
                {l.cost_type && l.cost_type !== "Normal" ? <span className="text-muted small"> ({l.cost_type})</span> : null}
              </td>
              {/* BLANK, not $0.00, where nothing was claimed before — 1.0's
                  own treatment, and the reason its prev columns recede: on
                  cycle 1 of job 73979, 27 of 40 lines have nothing there, and
                  printing a column of zeros buries the handful that moved. */}
              {showPrevious ? (
                <td className="text-end text-nowrap text-muted">{zeroBlank(l.percent_previous, `${Number(l.percent_previous) || 0}%`)}</td>
              ) : null}
              {showPrevious ? (
                <td className="text-end text-nowrap text-muted">{zeroBlank(l.previous_amount_claimed, money(l.previous_amount_claimed))}</td>
              ) : null}
              <td className="text-end text-nowrap">{money(l.quote_amount)}</td>
              <td className="text-end text-nowrap">{Number(l.percent_complete) || 0}%</td>
              <td className="text-end text-nowrap">{money(l.total_amount_claimed)}</td>
              {showPrevious ? (
                <td className="text-end text-nowrap">{zeroBlank(l.amount_claimed, money(l.amount_claimed))}</td>
              ) : null}
            </tr>
          ))}
          {/* The Quote column totals on its own row, as 1.0 prints it: the full
              contract value, which is what the percentages are a share OF. It
              sits apart from the money owed, so no other column totals here. */}
          {lines.length ? (
            <tr>
              <td colSpan={showPrevious ? 3 : 1} />
              <td className="text-end text-nowrap fw-semibold">{money(summary.quote_total)}</td>
              <td colSpan={showPrevious ? 3 : 2} />
            </tr>
          ) : null}
          {lines.length === 0 ? (
            <tr><td colSpan={showPrevious ? 7 : 4} className="text-center text-muted">No billable lines.</td></tr>
          ) : null}
        </tbody>
      </table>

      <div className="d-flex justify-content-end">
        <table style={{ minWidth: 320 }}>
          <tbody>
            {/* 1.0 SHOWS THE WORKING (Robert 2026-08-04): work claimed to date,
                then each earlier progress claim subtracted BY NUMBER, arriving
                at what is owed now. Copied because every deduction reconciles
                to an invoice the assessor has already been sent and paid — the
                per-line "This Claim" column above ties back to nothing outside
                this page, which is why 1.0 could do without it. */}
            {prior_claims.length ? (
              <>
                <tr>
                  <td className="text-muted pe-4">Subtotal of Progress Claim {progress_claim.claim_no}</td>
                  <td className="text-end">{money(summary.total_to_date)}</td>
                </tr>
                {prior_claims.map((p) => (
                  <tr key={p.claim_no}>
                    <td className="text-muted pe-4">Less Progress Claim {p.claim_no}</td>
                    <td className="text-end">{money(p.amount)}</td>
                  </tr>
                ))}
                <tr style={{ borderTop: "1px solid #dee2e6" }}>
                  <td className="text-muted pe-4">Total of Progress Claim</td>
                  <td className="text-end">{money(summary.subtotal)}</td>
                </tr>
              </>
            ) : null}
            <tr style={prior_claims.length ? { borderTop: "1px solid #dee2e6" } : undefined}>
              <td className="text-muted pe-4">Sub Total</td>
              <td className="text-end">{money(summary.subtotal)}</td>
            </tr>
            <tr>
              <td className="text-muted pe-4">GST ({Math.round(gst_rate * 100)}%)</td>
              <td className="text-end">{money(summary.gst)}</td>
            </tr>
            <tr className="fs-5 fw-bold" style={{ borderTop: "2px solid #212529" }}>
              <td className="pe-4">Total of Progress Claim {progress_claim.claim_no}</td>
              <td className="text-end">{money(summary.total)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* The progress claim previously promised payment "by the 20th of the
          month following invoice" while the tax invoice promised 10 days — two
          different terms under one letterhead. 1.0 says 10 days on both. */}
      <PaymentTerms reference={invoice_ref} />
    </div>
  );
}

export default InvoiceDocument;
